import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';

import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { ProductType } from '@tink/core/src/types';
import { useHistory } from 'react-router';
import { useGradualRollout as useGradualRolloutImport } from '../../../utilities/useGradualRollout';
import {
  ThirdPartyRedirectMachineView as ThirdPartyRedirectMachineViewImport,
  ThirdPartyRedirectMachineViewProps,
} from '../../../connectivity/screens/third-party-redirect/ThirdPartyRedirectMachineView';
import {
  AwaitingSupplementalInformation as AwaitingSupplementalInformationImport,
  AwaitingSupplementalInformationOwnProps,
} from '../../../income-check-v2/components/AwaitingSupplementalInformation';
import { stopPollingCredentials } from '../../../connectivity/services/credentials';
import {
  BankIdMachineView as BankIdMachineViewImport,
  BankIdMachineViewProps,
} from '../../../connectivity/screens/bank-id/BankIdMachineView';
import { ProviderEntity } from '../../../models/provider';
import {
  LoadingOverlay,
  LoadingOverlayProps,
} from '../../../core/screens/LoadingOverlay/LoadingOverlay';
import { AppState } from '../../../stores/redux-store';
import { Credentials, CredentialsEntity } from '../../../models/credentials';
import { applicationParameters } from '../../../utilities/application-parameters';
import {
  resetCredentials as resetCredentialsConnect,
  setCredentials as setCredentialsConnect,
} from '../../../stores/credentials-store';
import { useCredentialsPoller } from '../../hooks/useCredentialsPoller/useCredentialsPoller';
import { useProduct } from '../../ApplicationProviders/ProductProvider/ProductProvider';
import { useConfiguration } from '../../ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { setRedirectedCredentialsId as setRedirectedCredentialsIdConnect } from '../../../stores/app-state-store';
import { GradualReleaseLabels } from '../../../utilities/useGradualRolloutTypes';
import { PageView } from '../../../models/analytics';
import { BackButton } from '../../organisms/Header/HeaderMolecule';
import {
  AwaitingTppIframe as AwaitingTppIframeImport,
  AwaitingTppIframeProps,
} from './organisms/AwaitingThirdpartyAuthenticationContainer/AwaitingTppIframe/AwaitingTppIframe';
import { messages } from './messages';
import {
  AuthAwaitingThirdpartyAuthenticationContainer as AuthAwaitingThirdpartyAuthenticationContainerImport,
  AuthAwaitingThirdpartyAuthenticationContainerOwnProps,
} from './organisms/AwaitingThirdpartyAuthenticationContainer/AuthAwaitingThirdpartyAuthenticationContainer/AuthAwaitingThirdpartyAuthenticationContainer';

export type AwaitingCredentialsAuthProps = {
  provider: ProviderEntity;
  clientName?: string;
  children?: React.ReactNode;
  LoadingComponent?: React.ComponentType<LoadingOverlayProps>;
  AwaitingSupplementalInformation?: React.ComponentType<AwaitingSupplementalInformationOwnProps>;
  AuthAwaitingThirdpartyAuthenticationContainer?: React.ComponentType<AuthAwaitingThirdpartyAuthenticationContainerOwnProps>;
  AwaitingTppIframe?: React.ComponentType<AwaitingTppIframeProps>;
  BankIdMachineView?: React.ComponentType<BankIdMachineViewProps>;
  ThirdPartyRedirectMachineView?: React.ComponentType<ThirdPartyRedirectMachineViewProps>;
  isIframe?: boolean;
  isClientVerified: boolean;
  useGradualRollout?: typeof useGradualRolloutImport;
} & BackButton;

export const AwaitingCredentialsAuthPresenter: React.FC<
  AwaitingCredentialsAuthProps & PropsFromRedux
> = ({
  children,
  credentials,
  clientName,
  provider,
  LoadingComponent = LoadingOverlay,
  AwaitingSupplementalInformation = AwaitingSupplementalInformationImport,
  AuthAwaitingThirdpartyAuthenticationContainer = AuthAwaitingThirdpartyAuthenticationContainerImport,
  AwaitingTppIframe = AwaitingTppIframeImport,
  BankIdMachineView = BankIdMachineViewImport,
  ThirdPartyRedirectMachineView = ThirdPartyRedirectMachineViewImport,
  onBack,
  isIframe = applicationParameters.get('isIframe'),
  isClientVerified,
  setCredentials,
  resetCredentials,
  setRedirectedCredentialsId,
  useGradualRollout = useGradualRolloutImport,
}) => {
  const { isInTestGroup: isInThirdPartyRedirectTestGroup } = useGradualRollout(
    PageView.NO_SCREEN,
    GradualReleaseLabels.THIRD_PARTY_REDIRECT_MACHINE
  );

  const { isInTestGroup: isInLoadingScreenTestGroup } = useGradualRollout(
    PageView.NO_SCREEN,
    GradualReleaseLabels.CREDENTIALS_LOADING_SCREEN
  );

  const { productType, productDimension } = useProduct();
  const { data: configurationData } = useConfiguration();
  const { signCredentials } = useCredentialsPoller();
  const history = useHistory();

  if (!credentials || Credentials.hasError(credentials)) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  if (Credentials.isAwaitingSupplementalInformation(credentials)) {
    return <AwaitingSupplementalInformation provider={provider} credentials={credentials} />;
  }

  if (Credentials.isAwaitingThirdPartyAppAuthentication(credentials)) {
    const isInTestGroup = isInThirdPartyRedirectTestGroup({
      userId: credentials.userId,
    });

    if (isInTestGroup) {
      stopPollingCredentials();

      const onDone = (c: CredentialsEntity) => {
        setCredentials(c);
        signCredentials(c.id);

        if (!isIframe) {
          setRedirectedCredentialsId(c.id);
        }
      };

      const onBackWithResetCredentials = () => {
        resetCredentials();

        const isProviderPreselected = Boolean(applicationParameters.get('providerName'));

        if (!isProviderPreselected) {
          history.goBack();
        }
      };

      const clientData: ThirdPartyRedirectMachineViewProps['clientData'] = {
        clientName: clientName || '',
        isClientVerified,
      };

      return (
        <ThirdPartyRedirectMachineView
          selectedProvider={provider}
          clientData={clientData}
          initialCredentials={credentials}
          onDone={onDone}
          onError={setCredentials}
          onBack={onBack || onBackWithResetCredentials}
        />
      );
    }

    if (isIframe) {
      return (
        <AwaitingTppIframe
          credentials={credentials}
          clientName={clientName || ''}
          isClientVerified={isClientVerified}
        />
      );
    }

    return (
      <AuthAwaitingThirdpartyAuthenticationContainer
        credentials={credentials}
        provider={provider}
        clientName={clientName}
        onBack={onBack}
        isClientVerified={isClientVerified}
      />
    );
  }

  if (Credentials.isAwaitingMobileBankId(credentials)) {
    stopPollingCredentials();
    const onDone = (c: CredentialsEntity) => {
      setCredentials(c);

      if (Credentials.isUpdating(c)) {
        signCredentials(c.id);
      }
    };
    const onOpenBankId = (credentialsId: string) => {
      const isUsingTinkFlowFinishedScreen =
        userAgent.isAppleDevice && productType !== ProductType.PAY;

      if (isUsingTinkFlowFinishedScreen) setRedirectedCredentialsId(credentialsId);
    };

    return (
      <BankIdMachineView
        credentials={credentials}
        provider={provider}
        productType={productType}
        configurationData={configurationData}
        onDone={onDone}
        onError={setCredentials}
        onBack={onBack || resetCredentials}
        onOpenBankId={onOpenBankId}
        isPaymentSca={false}
        footerDisclaimer={undefined}
        onExecute={undefined}
      />
    );
  }

  if (Credentials.isUpdating(credentials)) {
    const message = isInLoadingScreenTestGroup({
      userId: credentials.userId,
      productType,
      productDimension,
    })
      ? messages.connectingToAlternative
      : messages.connectingTo;

    return (
      <LoadingComponent
        text={<FormattedMessage {...message} values={{ providerName: provider.displayName }} />}
      />
    );
  }

  // Handles the cases where credentials is:
  // `CREATED`, `AUTHENTICATING`, `UPDATING` and flashes by when `UPDATED` before parent change screen
  return <LoadingComponent />;
};

const mapStateToProps = (state: AppState) => {
  return {
    credentials: state.credentials.data,
    clientName: state.oauth.description?.clientName,
    isClientVerified: Boolean(state.oauth.description?.verified),
  };
};

const mapDispatchToProps = {
  setCredentials: setCredentialsConnect,
  resetCredentials: resetCredentialsConnect,
  setRedirectedCredentialsId: setRedirectedCredentialsIdConnect,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const AwaitingCredentialsAuth = connector(AwaitingCredentialsAuthPresenter);
